import {
  Combobox,
  ComboboxInput,
  ComboboxOption,
  ComboboxOptions,
} from '@headlessui/react';
import { useRef, useEffect, useCallback } from 'react';
import ChevronDownIcon from '@app/assets/icons/chevron-down.svg?react';
import clsx from 'clsx';

export interface SearchableSelectProps {
  className?: string;
  defaultValue?: string;
  error?: string;
  fetchNextPage: () => void;
  hasMore: boolean;
  isFetchingNextPage?: boolean;
  isLoading?: boolean;
  isOptional?: boolean;
  label?: string;
  name?: string;
  onChange: (value?: string) => void;
  onSearch: (query: string) => void;
  options: { value: string; label: string }[];
  placeholder?: string;
  sizeStyle?: 'sm' | 'md' | 'lg';
  value?: string;
}

const getStyle = (size: string) => {
  switch (size) {
    case 'sm': {
      return 'text-sm py-2 px-3';
    }
    case 'md': {
      return 'text-normal py-3 px-4';
    }
    case 'lg': {
      return 'text-lg py-4 px-5';
    }
  }
};

export const SearchableSelect = ({
  className,
  error,
  fetchNextPage,
  hasMore,
  isFetchingNextPage = false,
  isLoading = false,
  isOptional,
  label,
  name,
  onChange,
  onSearch,
  options,
  placeholder,
  sizeStyle = 'sm',
  value,
}: SearchableSelectProps) => {
  const observerRef = useRef<IntersectionObserver | null>(null);

  const lastOptionRef = useCallback(
    (node: HTMLDivElement | null) => {
      if (isFetchingNextPage || isLoading) return;

      if (observerRef.current) {
        observerRef.current.disconnect();
      }

      if (!node || !hasMore) return;

      observerRef.current = new IntersectionObserver(
        (entries) => {
          const [entry] = entries;

          if (entry.isIntersecting && hasMore && !isFetchingNextPage) {
            fetchNextPage();
          }
        },
        {
          threshold: 0.1,
          rootMargin: '100px',
        }
      );

      observerRef.current.observe(node);
    },
    [hasMore, isFetchingNextPage, isLoading, fetchNextPage]
  );

  useEffect(() => {
    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, []);

  const displayValue = (item: string | null | undefined) => {
    if (!item) return '';
    return options?.find((o) => o.value === item)?.label || '';
  };

  return (
    <div className={clsx('relative', className)}>
      <Combobox
        immediate
        value={value || null}
        onChange={(value) => onChange(value || undefined)}
        onClose={() => onSearch('')}
      >
        {() => (
          <>
            {label && (
              <label
                htmlFor={name}
                className="font-medium text-sm inline-block mb-1"
              >
                {label}{' '}
                {isOptional && (
                  <span className="text-base-coolGray">(Optional)</span>
                )}
              </label>
            )}
            <div className="relative w-full">
              <div className="relative w-full">
                <ComboboxInput
                  displayValue={displayValue}
                  onChange={(event) => onSearch(event.target.value ?? '')}
                  placeholder={placeholder}
                  className={clsx(
                    `group bg-gray-100 border-0 rounded-lg py-3 px-4 w-full outline-brand-baseBlue outline-1 ${getStyle(sizeStyle)} pr-10 text-gray-600 placeholder:text-gray-500 focus:bg-gray-200 focus:outline-none`,
                    { 'border border-system-error': error }
                  )}
                />
                <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                  {isLoading ? (
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-gray-400"></div>
                  ) : (
                    <ChevronDownIcon className="w-5 h-5 text-gray-400" />
                  )}
                </div>
              </div>
            </div>
            <ComboboxOptions className="absolute left-0 right-0 z-[9999] w-full mt-1 rounded-lg shadow-lg bg-white max-h-64 overflow-y-auto border border-gray-200">
              {isLoading && options.length === 0 ? (
                <div className="p-4 text-sm text-gray-500 text-center">
                  Loading...
                </div>
              ) : options.length === 0 ? (
                <div className="p-4 text-sm text-gray-500 text-center">
                  No records found matching filters
                </div>
              ) : (
                <>
                  {options.filter(Boolean).map((option, index) => {
                    const isLast = index === options.length - 1;
                    return (
                      <div
                        key={option?.value || index}
                        ref={isLast ? lastOptionRef : null}
                      >
                        <ComboboxOption
                          value={option?.value || ''}
                          className="data-[focus]:bg-brand-lightBlue4 p-4 text-sm border-b border-b-base-seasalt cursor-pointer"
                        >
                          {option?.label || ''}
                        </ComboboxOption>
                      </div>
                    );
                  })}
                  {isFetchingNextPage && (
                    <div className="p-4 text-sm text-gray-500 text-center">
                      Loading more...
                    </div>
                  )}
                </>
              )}
            </ComboboxOptions>
          </>
        )}
      </Combobox>
      {error && (
        <p role="alert" className="text-system-error text-sm text-left pt-1">
          {error}
        </p>
      )}
    </div>
  );
};
